import React, { useEffect, memo, useCallback } from 'react';
import { Button, Grid, Header, Message, Icon } from 'semantic-ui-react';
import { useSharedData } from '../../hooks/core/useZoneHooks';

/**
 * LitGroupsList-enh component
 * Enhanced version of LitGroupsList with improved UI matching LitGroupListManager-bcw
 * Displays a list of literary groups
 * @param {Object} props - Component props
 * @param {Function} props.onLitGroupSelect - Callback when a literary group is selected
 * @param {Function} props.onDeleteLitGroup - Callback when a literary group delete is requested
 * @returns {JSX.Element} LitGroupsList-enh component
 */
const LitGroupsListEnh = memo(({ onLitGroupSelect, onDeleteLitGroup, onNewLiteraryGroup }) => {
  const sharedData = useSharedData();

  // Extract data from new hook
  const {
    litGroups,
    selections,
    loading,
    errors
  } = sharedData;

  // Map to old interface for compatibility
  const litGroupsArray = Array.isArray(litGroups) ? litGroups : [];
  const litGroupId = selections?.litGroupId;
  const litGroupsLoading = loading?.litGroups ? 'loading' : 'idle';
  const litGroupsError = errors?.litGroups;

  // Get functions from shared data
  const fetchLitGroups = sharedData.actions.fetchLitGroups;
  const setLitGroupSelection = sharedData.actions.setLitGroupSelection;

  // Fetch literary groups when the component mounts
  useEffect(() => {
    fetchLitGroups();
  }, [fetchLitGroups]);

  // Handle literary group selection
  const handleLitGroupSelect = useCallback((litGroup) => {
    setLitGroupSelection(litGroup.uuid);
    if (onLitGroupSelect) {
      onLitGroupSelect(litGroup);
    }
  }, [setLitGroupSelection, onLitGroupSelect]);

  // Handle delete literary group
  const handleDeleteLitGroup = useCallback((e, litGroup) => {
    e.stopPropagation(); // Prevent row selection
    console.log('Delete literary group:', litGroup.name);

    // Call the parent's delete handler to open the enhanced modal
    if (onDeleteLitGroup) {
      onDeleteLitGroup(litGroup);
    }
  }, [onDeleteLitGroup]);

  // Get the type of a literary group
  const getLitGroupType = useCallback((group) => {
    return group.type && typeof group.type === 'object' ? group.type.name : group.type || 'N/A';
  }, []);

  return (
    <div className="bw-lit-groups-container">
      <Header className="bw-hdr-with-prompt" as="h3">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          marginBottom: '10px'
        }}>
          <span>Literary Groups</span>
          <div style={{ width: '30px' }}>
            {/* {litGroupsLoading === 'loading' ?
            <div className="bw-book-message" style={{
              backgroundColor: '#F8FFFF',
              border: 'solid 1px #a9d5de',
              marginLeft: '10px'
            }}>
              <p>Loading groups...</p>
            </div>
            : ""} */}
          </div>
          <Button
            primary
            icon
            labelPosition="left"
            onClick={onNewLiteraryGroup}
            style={{ marginTop: '3px', marginRight: '0px', alignItems: 'right' }}
          >
            <Icon name="add" />
            New Literary Group
          </Button>
        </div>
      </Header>
      {/* Literary Groups list */}
      {litGroupsLoading === 'failed' ? (
        <Message negative>
          <Message.Header>Error Loading Literary Groups</Message.Header>
          <p>{litGroupsError || 'An unknown error occurred'}</p>
        </Message>
      ) : litGroups && litGroups.length > 0 ? (
        <div className="ui segment bw-literary-group-list">
          {/* Header row */}
          <Grid>
            <Grid.Row className="bw-list-header-row">
              <Grid.Column className='bw-noRightPad' width={9}>Name</Grid.Column>
              <Grid.Column width={7}>Type</Grid.Column>
            </Grid.Row>

            {/* Literary Group rows — deduplicated by UUID */}
            {litGroups
              .filter((lg, idx, self) => self.findIndex(l => l.uuid === lg.uuid) === idx)
              .map((litGroup) => (
                <Grid.Row
                  key={litGroup.uuid}
                  className="bw-list-item-row"
                  style={{
                    backgroundColor: litGroupId === litGroup.uuid ? '#ebf3fb' : 'transparent'
                  }}
                  onClick={() => handleLitGroupSelect(litGroup)}
                >
                  <Grid.Column width={9}>{litGroup.name}</Grid.Column>
                  <Grid.Column className='bw-noRightPad' width={5}>{getLitGroupType(litGroup)}</Grid.Column>
                  <Grid.Column width={2} textAlign="right">
                    <Icon
                      name="trash alternate"
                      color="red"
                      className="bw-trash-icon"
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => handleDeleteLitGroup(e, litGroup)}
                    />
                  </Grid.Column>
                </Grid.Row>
              ))}
          </Grid>
        </div>
      ) : (
        <div className="bw-empty-list-message">
          <p>No literary groups found. Create a new literary group to get started.</p>
        </div>
      )}
    </div>
  );
});

export default LitGroupsListEnh;
